/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Only DateUtils should ever import this class directly!
// There is NO backward compatibility support for this module.

// Mostly holds ab initio specific date(time) formatter implementations

import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { DateTimeFormatterDelegator } from "abweb/utils/date/DateTimeStructs";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { getCurrentDate } from "abweb/utils/date/internal/CurrentDateFactory";

// If inject these dynamically then no longer need import from DateTimeUtils.  Still get cyclic cypress spec issue!
// const DATE_SHORT: DateTimeFormatter = null!;
// const DATE_MONTH_AND_DAY: DateTimeFormatter = null!;
// const TIME_WITH_MINUTES: DateTimeFormatter = null!;
// const TIME_WITH_SECONDS: DateTimeFormatter = null!;
// const DATETIME_WITH_MINUTES: DateTimeFormatter = null!;
// const DATETIME_WITH_SECONDS: DateTimeFormatter = null!;

/**
 * Calculates time difference between the two given dates and returns it formatted as in
 * "2 days 1 hour 20 minutes 35 seconds". The two date objects can be given in any order; that
 * is, it doesn't matter which one is earlier or later.
 * @param {Date} date1
 * @param {Date} date2
 */
export function formatDifference(date1, date2) {
  if (!date1 || !date2) {
    return "";
  }
  let v1 = date1.valueOf();
  let v2 = date2.valueOf();
  let diff = new Date(v1 > v2 ? v1 - v2 : v2 - v1);
  let buf = [];
  let numYears = diff.getUTCFullYear() - 1970;
  numYears > 0 && buf.push(numYears === 1 ? AbWebResources.timediff_oneYear() : AbWebResources.timediff_years(numYears.toString()));
  let numMonths = diff.getUTCMonth();
  numMonths > 0 && buf.push(numMonths === 1 ? AbWebResources.timediff_oneMonth() : AbWebResources.timediff_months(numMonths.toString()));
  let numDays = diff.getUTCDate() - 1;
  numDays > 0 && buf.push(numDays === 1 ? AbWebResources.timediff_oneDay() : AbWebResources.timediff_days(numDays.toString()));
  let numHours = diff.getUTCHours();
  numHours > 0 && buf.push(numHours === 1 ? AbWebResources.timediff_oneHour() : AbWebResources.timediff_hours(numHours.toString()));
  let numMinutes = diff.getUTCMinutes();
  numMinutes > 0 && buf.push(numMinutes === 1 ? AbWebResources.timediff_oneMinute() : AbWebResources.timediff_minutes(numMinutes.toString()));
  let numSeconds = diff.getUTCSeconds();
  numSeconds > 0 && buf.push(numSeconds === 1 ? AbWebResources.timediff_oneSecond() : AbWebResources.timediff_seconds(numSeconds.toString()));
  return buf.length === 0 ? AbWebResources.timediff_seconds("0") : buf.join(" ");
}

/**
 * Returns a date formatted relative to the current date. If the delta between the two dates is more than seven days,
 * a short-date format is used. Otherwise, the date is reduced to something like "3 days ago". If the date is in
 * the future, a short-date format is used.
 * @param value The date to format.
 * @returns {string} The formatted date.
 */
export const formatRelativeDate = new DateTimeFormatterDelegator(value => {
  if (!value) {
    return "";
  }
  const curDate = getCurrentDate();
  const deltaSeconds = Math.floor((curDate.getTime() - value.getTime()) / 1000);
  if (deltaSeconds < 0) {
    return DateTimeUtils.DATE_SHORT.format(value);
  }

  // if we support "... years ago"
  // deltaAsUnit = Math.floor(deltaSeconds / (1 /*year*/ * 365 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60/*seconds*/));
  // if ((deltaAsUnit) > 0)
  // {
  //   if (deltaAsUnit === 1)
  //   {
  //     return AbWebResources.dateFormatYearAgo", deltaAsUnit);
  //   }
  //   return AbWebResources.dateFormatYearsAgo", deltaAsUnit);
  // }

  // if we support "... months ago"
  // // Note this estimates a month as 30 days...
  // deltaAsUnit = Math.floor(deltaSeconds / (1 /*month*/ * 30 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60/*seconds*/));
  // if (deltaAsUnit > 0)
  // {
  //   if (deltaAsUnit === 1)
  //   {
  //     return AbWebResources.dateFormatMonthAgo", deltaAsUnit);
  //   }
  //   return AbWebResources.dateFormatMonthsAgo", deltaAsUnit);
  // }

  const startOfToday = new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 1 /*d*/ * 24 /*h*/ * 60 /*m*/ * 60 /*s*/ * 1000 /*ms*/);
  if (value.getTime() >= startOfToday.getTime()) {
    // This happened some time earlier today. We've already handled future dates, so the only chance is that this
    // happened between the start of today and now.
    return AbWebResources.dateFormatToday();
  }
  if (value.getTime() >= startOfYesterday.getTime()) {
    // This happened some time yesterday. We've already handled future dates and today, so the only chance is that
    // this happened between the start of yesterday and the start of today.
    return AbWebResources.dateFormatYesterday();
  }

  // To compute how many days ago this happened, compute the delta between the start of the day it happened and the
  // start of today.
  const startOfDayOfChange = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const numDays = Math.floor((startOfToday.getTime() - startOfDayOfChange.getTime()) / (1 /*d*/ * 24 /*h*/ * 60 /*m*/ * 60 /*s*/ * 1000 /*ms*/));
  if (numDays > 6) {
    // A week or more ago, just format as a short date.
    return DateTimeUtils.DATE_SHORT.format(value);
  }
  return AbWebResources.dateFormatDaysAgo(numDays.toString());
});

/**
 * Returns a date formatted relative to the current date. If the delta between the two dates is more than seven days,
 * a short-date format is used. Otherwise, the date is reduced to something like "3 minutes ago". If the date is in
 * the future, a short-date format is used.
 * @param value The date to format.
 * @returns {string} The formatted date.
 */
export const formatRelativeDateTime = new DateTimeFormatterDelegator(value => {
  if (!value) {
    return "";
  }
  const curDate = getCurrentDate();
  const deltaSeconds = Math.floor((curDate.getTime() - value.getTime()) / 1000);
  if (deltaSeconds < 0) {
    return DateTimeUtils.DATETIME_WITH_SECONDS.format(value);
  }
  let deltaAsUnit;

  // if we support "... years ago"
  // deltaAsUnit = Math.floor(deltaSeconds / (1 /*year*/ * 365 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60/*seconds*/));
  // if ((deltaAsUnit) > 0)
  // {
  //   if (deltaAsUnit === 1)
  //   {
  //     return AbWebResources.dateFormatYearAgo(deltaAsUnit);
  //   }
  //   return AbWebResources.dateFormatYearsAgo(deltaAsUnit);
  // }

  // if we support "... months ago"
  // // Note this estimates a month as 30 days...
  // deltaAsUnit = Math.floor(deltaSeconds / (1 /*month*/ * 30 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60/*seconds*/));
  // if (deltaAsUnit > 0)
  // {
  //   if (deltaAsUnit === 1)
  //   {
  //     return AbWebResources.dateFormatMonthAgo(deltaAsUnit);
  //   }
  //   return AbWebResources.dateFormatMonthsAgo(deltaAsUnit);
  // }

  const startOfToday = new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate());
  // To compute how many days ago this happened, compute the delta between the start of the day it happened and the
  // start of today.
  const startOfDayOfChange = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const numDays = Math.floor((startOfToday.getTime() - startOfDayOfChange.getTime()) / (1 /*d*/ * 24 /*h*/ * 60 /*m*/ * 60 /*s*/ * 1000 /*ms*/));
  if (numDays > 6) {
    return DateTimeUtils.DATETIME_WITH_SECONDS.format(value);
  }
  if (numDays > 1) {
    return AbWebResources.dateFormatDaysAgo(numDays.toString());
  }
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    if (numDays === 1 && deltaAsUnit >= 5) {
      return AbWebResources.dateFormatYesterday();
    }
    if (deltaAsUnit === 1) {
      return AbWebResources.dateFormatHourAgo(deltaAsUnit.toString());
    }
    return AbWebResources.dateFormatHoursAgo(deltaAsUnit.toString());
  }
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    if (deltaAsUnit === 1) {
      return AbWebResources.dateFormatMinuteAgo(deltaAsUnit.toString());
    }
    return AbWebResources.dateFormatMinutesAgo(deltaAsUnit.toString());
  }

  // if we support "... seconds ago" - note that we can have a threshold for how many seconds before we just revert
  // to "moments ago"
  // deltaAsUnit = deltaSeconds;
  // if (deltaAsUnit > 5)
  // {
  //   return AbWebResources.dateFormatSecondsAgo(deltaAsUnit);
  // }
  return AbWebResources.dateFormatMomentsAgo();
});

/**
 * Returns a date formatted relative to the current date. If the delta between the two dates is more than seven days,
 * a short-date format is used. Otherwise, the date is reduced to something like "3 minutes ago". If the date is in
 * the future, a short-date format is used.
 * @param value The date to format.
 * @returns {string} The formatted date.
 */
export const formatRelativeDateTimeAbbreviated = new DateTimeFormatterDelegator(value => {
  if (!value) {
    return "";
  }
  const curDate = getCurrentDate();
  const deltaSeconds = Math.floor((curDate.getTime() - value.getTime()) / 1000);
  if (deltaSeconds < 0) {
    return DateTimeUtils.DATETIME_WITH_SECONDS.format(value);
  }
  let deltaAsUnit;

  // if we support "... years ago"
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*year*/ * 365 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    if (deltaAsUnit === 1) {
      return AbWebResources.dateFormatYearAgo_abbreviated();
    }
    return AbWebResources.dateFormatYearsAgo_abbreviated();
  }

  // if we support "... months ago"
  // // Note this estimates a month as 30 days...
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*month*/ * 30 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    return AbWebResources.dateFormatMonthsAgo_abbreviated(deltaAsUnit.toString());
  }
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*week*/ * 7 /*days*/ * 24 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    //weeks ago
    return AbWebResources.dateFormatWeeksAgo_abbreviated(deltaAsUnit.toString());
  }
  const startOfToday = new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate());
  // To compute how many days ago this happened, compute the delta between the start of the day it happened and the
  // start of today.
  const startOfDayOfChange = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const numDays = Math.floor((startOfToday.getTime() - startOfDayOfChange.getTime()) / (1 /*d*/ * 24 /*h*/ * 60 /*m*/ * 60 /*s*/ * 1000 /*ms*/));
  if (numDays > 1) {
    return AbWebResources.dateFormatDaysAgo_abbreviated(numDays.toString());
  }
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    if (numDays === 1 && deltaAsUnit >= 5) {
      return AbWebResources.dateFormatDaysAgo_abbreviated(numDays.toString());
    }
    return AbWebResources.dateFormatHoursAgo_abbreviated(deltaAsUnit.toString());
  }
  deltaAsUnit = Math.floor(deltaSeconds / (1 /*minutes*/ * 60 /*seconds*/));
  if (deltaAsUnit > 0) {
    return AbWebResources.dateFormatMinutesAgo_abbreviated(deltaAsUnit.toString());
  }

  // if we support "... seconds ago" - note that we can have a threshold for how many seconds before we just revert
  // to "moments ago"
  // deltaAsUnit = deltaSeconds;
  // if (deltaAsUnit > 5)
  // {
  //   return AbWebResources.dateFormatSecondsAgo(deltaAsUnit);
  // }
  return AbWebResources.dateFormatMomentsAgo();
});

/**
 * Returns a date formatted relative to the current date with time-only for today and "Yesterday Time"
 * for yesterday dates. If the date is from the current year, then "Month, Day Time" is returned. For
 * all other dates, including dates in the future, a short-date-time format is used.
 * @param value The date to format.
 * @returns {string} The formatted date.
 */
export function formatRelativeDateTimeEx(value, includeSeconds) {
  if (!value) {
    return "";
  }

  // get time value
  let timeString = includeSeconds ? DateTimeUtils.TIME_WITH_SECONDS.format(value) : DateTimeUtils.TIME_WITH_MINUTES.format(value);
  const curDate = getCurrentDate();

  // handle future dates
  const deltaSeconds = Math.floor((curDate.getTime() - value.getTime()) / 1000);
  if (deltaSeconds < 0) {
    return includeSeconds ? DateTimeUtils.DATETIME_WITH_SECONDS.format(value) : DateTimeUtils.DATETIME_WITH_MINUTES.format(value);
  }

  // To compute how many days ago this happened, compute the delta between the start of the input date and the
  // start of the current date.
  const startOfToday = new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate());
  const startOfDayOfChange = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const numDays = Math.floor((startOfToday.getTime() - startOfDayOfChange.getTime()) / (24 /*h*/ * 60 /*m*/ * 60 /*s*/ * 1000 /*ms*/));
  if (numDays === 0) {
    return timeString;
  } else if (numDays === 1) {
    return `${AbWebResources.dateFormatYesterday()}, ${timeString}`;
  }

  // Check whether the input date is from this year or not.
  const thisYear = curDate.getFullYear();
  const inputYear = value.getFullYear();
  if (thisYear === inputYear) {
    return `${DateTimeUtils.DATE_MONTH_AND_DAY.format(value)}, ${timeString}`;
  }

  // for all other cases, return a regular formatting
  return includeSeconds ? DateTimeUtils.DATETIME_WITH_SECONDS.format(value) : DateTimeUtils.DATETIME_WITH_MINUTES.format(value);
}
//# sourceMappingURL=DateUtilsInternals.js.map
